import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  TablePagination,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search,
  Add,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { STATUS_COLORS } from '../helpers/constants';
import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableStyles,
  getTableCellStyles,
  getTableRowStyles,
  formatStatusText
} from '../styles/tableStyles';
import { formatTableDate } from '../utils/dateUtils';
import { adminAPI } from '../services/api';
import InviteUserModal from './InviteUserModal';

interface User {
  id: string;
  name?: string;
  email: string;
  role: string;
  status: string;
  lastLogin?: string;
  isRootAdmin?: boolean;
}

const UserManagement: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers();
      setUsers(response.data || []);
    } catch (err: any) {
      // On error, show empty users list (will display "No users found")
      setUsers([]);
      // Silently handle error - don't show error message to user
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      showSuccess('User deleted successfully');
      fetchUsers();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete user';
      showError(errorMessage);
    }
  };

  const handleInviteSuccess = () => {
    fetchUsers();
    setInviteModalOpen(false);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.role || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get current page data
  const currentUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'default';
    const statusKey = status.toUpperCase() as keyof typeof STATUS_COLORS;
    return STATUS_COLORS[statusKey] || 'default';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header with search and new user button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ 
            width: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-focused': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
              },
            },
          }}
        />
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setInviteModalOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          ADD USER
        </Button>
      </Box>

      {/* User table */}
      <Paper sx={getTableContainerStyles()}>
        <TableContainer>
          <Table {...getTableStyles()}>
            <TableHead>
              <TableRow sx={getTableRowStyles()}>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Name
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Email
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Role
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Status
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Last Login
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={6} align="center" sx={getTableCellStyles()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : currentUsers.length === 0 ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={6} align="center" sx={getTableCellStyles()}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                currentUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={getTableRowStyles()}
                  >
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {user.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {user.role || 'User'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Chip 
                        label={formatStatusText(user.status || 'INACTIVE')} 
                        color={getStatusColor(user.status || 'INACTIVE') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {user.lastLogin ? formatTableDate(user.lastLogin, 'created') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.id)}
                        color="error"
                        disabled={user.isRootAdmin}
                        title={user.isRootAdmin ? 'Cannot delete root admin' : 'Delete user'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </Box>
  );
};

export default UserManagement;
